// PostAllocationDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  InputAdornment,
  Box,
  Divider,
  Paper,
  Chip,
  IconButton,
  Card,
  CardContent,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ArrowBack,
  Save,
  CheckCircle,
  Cancel,
  MonetizationOn,
  Assessment,
  ExpandMore,
  Add,
  Delete,
  AccountBalance
} from '@mui/icons-material';

const PostAllocationDialog = ({ 
  open, 
  onClose, 
  selectedGroup,
  groupMembers,
  onDisburseLoan,
  onCancelLoan,
  onSaveGroupOthers,
  onViewPerformanceAnalysis
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [approvedLoans, setApprovedLoans] = useState([]);
  const [groupOthersData, setGroupOthersData] = useState({
    items: [],
    totalAmount: 0,
    descriptions: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Mock data for approved loans
  const mockApprovedLoans = [
    {
      id: 1,
      member_id: 101,
      member_name: 'John Doe',
      member_phone: '+254712345678',
      loan_type: 'regular',
      amount: 50000,
      approved_date: '2024-08-28',
      requested_date: '2024-08-25',
      purpose: 'Business expansion',
      interest_rate: 5,
      duration_months: 6,
      status: 'approved'
    },
    {
      id: 2,
      member_id: 102,
      member_name: 'Jane Smith',
      member_phone: '+254723456789',
      loan_type: 'emergency',
      amount: 25000,
      approved_date: '2024-08-29',
      requested_date: '2024-08-28',
      purpose: 'Medical emergency',
      interest_rate: 3,
      duration_months: 3,
      status: 'approved'
    },
    {
      id: 3,
      member_id: 103,
      member_name: 'Peter Kamau',
      member_phone: '+254734567890',
      loan_type: 'regular',
      amount: 75000,
      approved_date: '2024-08-27',
      requested_date: '2024-08-24',
      purpose: 'School fees',
      interest_rate: 5,
      duration_months: 8,
      status: 'approved'
    }
  ];

  const steps = [
    'Loan Disbursement',
    'Group Others Recording'
  ];

  useEffect(() => {
    if (open && selectedGroup) {
      // In real implementation, filter loans by group members
      const groupMemberIds = groupMembers.map(member => member.id);
      const groupLoans = mockApprovedLoans.filter(loan => 
        groupMemberIds.includes(loan.member_id)
      );
      setApprovedLoans(groupLoans);
    }
  }, [open, selectedGroup, groupMembers]);

  // Handle loan disbursement
  const handleDisburseLoan = async (loanId) => {
    setSubmitting(true);
    try {
      const loan = approvedLoans.find(l => l.id === loanId);
      console.log('Disbursing loan:', loan);
      
      // Call parent handler or API
      if (onDisburseLoan) {
        await onDisburseLoan(loanId);
      }
      
      // Update local state
      setApprovedLoans(prev => prev.map(loan => 
        loan.id === loanId ? { ...loan, status: 'disbursed' } : loan
      ));
      
      alert('Loan disbursed successfully!');
    } catch (error) {
      console.error('Error disbursing loan:', error);
      alert('Error disbursing loan: ' + (error.message || 'Unknown error'));
    } finally {
      setSubmitting(false);
    }
  };

  // Handle loan cancellation
  const handleCancelLoan = async (loanId) => {
    setSubmitting(true);
    try {
      const loan = approvedLoans.find(l => l.id === loanId);
      console.log('Cancelling loan:', loan);
      
      // Call parent handler or API
      if (onCancelLoan) {
        await onCancelLoan(loanId);
      }
      
      // Update local state
      setApprovedLoans(prev => prev.map(loan => 
        loan.id === loanId ? { ...loan, status: 'cancelled' } : loan
      ));
      
      alert('Loan cancelled successfully!');
    } catch (error) {
      console.error('Error cancelling loan:', error);
      alert('Error cancelling loan: ' + (error.message || 'Unknown error'));
    } finally {
      setSubmitting(false);
    }
  };

  // Handle group others items
  const addGroupOthersItem = () => {
    setGroupOthersData({
      ...groupOthersData,
      items: [...groupOthersData.items, { description: '', amount: 0 }]
    });
  };

  const removeGroupOthersItem = (index) => {
    const newItems = groupOthersData.items.filter((_, i) => i !== index);
    const newTotal = newItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    setGroupOthersData({
      ...groupOthersData,
      items: newItems,
      totalAmount: newTotal,
      descriptions: newItems.map(item => item.description).filter(desc => desc.trim()).join(', ')
    });
  };

  const updateGroupOthersItem = (index, field, value) => {
    const newItems = [...groupOthersData.items];
    if (field === 'amount') {
      newItems[index][field] = parseFloat(value) || 0;
    } else {
      newItems[index][field] = value;
    }
    
    const newTotal = newItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const newDescriptions = newItems.map(item => item.description).filter(desc => desc.trim()).join(', ');
    
    setGroupOthersData({
      items: newItems,
      totalAmount: newTotal,
      descriptions: newDescriptions
    });
  };

  // Handle saving group others
  const handleSaveGroupOthers = async () => {
    if (groupOthersData.totalAmount <= 0) {
      alert('Please add at least one item with an amount greater than 0');
      return;
    }

    setSubmitting(true);
    try {
      console.log('Saving group others data:', groupOthersData);
      
      // Call parent handler or API
      if (onSaveGroupOthers) {
        await onSaveGroupOthers(selectedGroup.id, groupOthersData);
      }
      
      alert('Group others recorded successfully!');
      
      // Reset form
      setGroupOthersData({
        items: [],
        totalAmount: 0,
        descriptions: ''
      });
      
    } catch (error) {
      console.error('Error saving group others:', error);
      alert('Error saving group others: ' + (error.message || 'Unknown error'));
    } finally {
      setSubmitting(false);
    }
  };

  // Handle view performance analysis
  const handleViewPerformanceAnalysis = () => {
    if (onViewPerformanceAnalysis) {
      onViewPerformanceAnalysis(selectedGroup);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Get loan type color
  const getLoanTypeColor = (type) => {
    switch (type) {
      case 'emergency': return 'error';
      case 'regular': return 'primary';
      default: return 'default';
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'warning';
      case 'disbursed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  // Handle dialog close
  const handleDialogClose = () => {
    setCurrentStep(0);
    setGroupOthersData({
      items: [],
      totalAmount: 0,
      descriptions: ''
    });
    onClose();
  };

  // Check if we can proceed to next step
  const canProceedToNext = () => {
    if (currentStep === 0) {
      // Can proceed if no pending loans or all have been processed
      return approvedLoans.every(loan => loan.status !== 'approved');
    }
    return false;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleDialogClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { 
          height: '90vh',
          maxHeight: '90vh',
          width: '95%',
          maxWidth: '1200px',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="h5">
            Post-Allocation Tasks - {selectedGroup?.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Complete remaining tasks for this group session
          </Typography>
          
          {/* Stepper */}
          <Box sx={{ mt: 2 }}>
            <Stepper activeStep={currentStep} alternativeLabel>
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 1, flex: 1, overflow: 'auto' }}>
        {currentStep === 0 && (
          // Step 1: Loan Disbursement
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MonetizationOn color="primary" />
              Approved Loans for Disbursement
            </Typography>
            
            {approvedLoans.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                No approved loans found for this group.
              </Alert>
            ) : (
              <Box sx={{ mt: 2 }}>
                {approvedLoans.map((loan) => (
                  <Card key={loan.id} sx={{ mb: 2, border: 1, borderColor: 'divider' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography variant="h6" gutterBottom>
                            {loan.member_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {loan.member_phone}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <Chip 
                              label={loan.loan_type.toUpperCase()} 
                              color={getLoanTypeColor(loan.loan_type)}
                              size="small"
                            />
                            <Chip 
                              label={loan.status.toUpperCase()} 
                              color={getStatusColor(loan.status)}
                              size="small"
                            />
                          </Box>
                        </Box>
                        <Typography variant="h5" color="primary" fontWeight="bold">
                          {formatCurrency(loan.amount)}
                        </Typography>
                      </Box>

                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Purpose: {loan.purpose}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="body2" color="text.secondary">
                            Interest: {loan.interest_rate}%
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <Typography variant="body2" color="text.secondary">
                            Duration: {loan.duration_months} months
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Requested: {new Date(loan.requested_date).toLocaleDateString()}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Approved: {new Date(loan.approved_date).toLocaleDateString()}
                          </Typography>
                        </Grid>
                      </Grid>

                      {loan.status === 'approved' && (
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={<CheckCircle />}
                            onClick={() => handleDisburseLoan(loan.id)}
                            disabled={submitting}
                            size="small"
                          >
                            Disburse
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<Cancel />}
                            onClick={() => handleCancelLoan(loan.id)}
                            disabled={submitting}
                            size="small"
                          >
                            Cancel
                          </Button>
                        </Box>
                      )}

                      {loan.status === 'disbursed' && (
                        <Alert severity="success" sx={{ mt: 1 }}>
                          <Typography variant="body2">
                            Loan disbursed successfully
                          </Typography>
                        </Alert>
                      )}

                      {loan.status === 'cancelled' && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                          <Typography variant="body2">
                            Loan has been cancelled
                          </Typography>
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
          </Box>
        )}

        {currentStep === 1 && (
          // Step 2: Group Others Recording
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccountBalance color="primary" />
              Record Other Group Collections
            </Typography>
            
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
              Record any additional amounts collected for this group during this period (e.g., registration fees, penalties, special contributions).
            </Typography>

            {/* Group Others Items */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  Other Collections
                </Typography>
                <Button
                  startIcon={<Add />}
                  onClick={addGroupOthersItem}
                  variant="outlined"
                  size="small"
                >
                  Add Item
                </Button>
              </Box>

              {groupOthersData.items.length === 0 ? (
                <Alert severity="info">
                  Click "Add Item" to record other collections for this group.
                </Alert>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {groupOthersData.items.map((item, index) => (
                    <Paper key={index} sx={{ p: 2, bgcolor: 'grey.50', border: 1, borderColor: 'divider' }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label={`Description ${index + 1}`}
                            value={item.description || ''}
                            onChange={(e) => updateGroupOthersItem(index, 'description', e.target.value)}
                            placeholder="e.g., Registration fees, Penalties, etc."
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                          <TextField
                            fullWidth
                            label="Amount"
                            type="number"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">KES</InputAdornment>,
                            }}
                            value={item.amount || ''}
                            onChange={(e) => updateGroupOthersItem(index, 'amount', e.target.value)}
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={1}>
                          <IconButton
                            onClick={() => removeGroupOthersItem(index)}
                            color="error"
                            size="small"
                          >
                            <Delete />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Box>
              )}

              {/* Others Summary */}
              {groupOthersData.items.length > 0 && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Summary
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Total Amount:</strong> {formatCurrency(groupOthersData.totalAmount)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Items:</strong> {groupOthersData.descriptions || 'No descriptions provided'}
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* Save Group Others */}
            {groupOthersData.items.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSaveGroupOthers}
                  disabled={submitting || groupOthersData.totalAmount <= 0}
                  size="large"
                >
                  {submitting ? 'Saving...' : 'Save Group Others'}
                </Button>
              </Box>
            )}

            {/* Performance Analysis Button */}
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Session Complete
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                All member allocations have been completed. View group performance analysis or close this session.
              </Typography>
              
              <Button
                variant="contained"
                color="secondary"
                startIcon={<Assessment />}
                onClick={handleViewPerformanceAnalysis}
                size="large"
                sx={{ mb: 2 }}
              >
                View Performance Analysis
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          
          {/* Left Side - Navigation */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button 
              onClick={handleDialogClose}
              disabled={submitting}
              size="small"
            >
              Close Session
            </Button>
            
            {currentStep > 0 && (
              <Button
                startIcon={<ArrowBack />}
                onClick={() => setCurrentStep(currentStep - 1)}
                disabled={submitting}
                size="small"
              >
                Previous
              </Button>
            )}
          </Box>

          {/* Center - Status Info */}
          <Box sx={{ textAlign: 'center' }}>
            {currentStep === 0 && (
              <Typography variant="body2" color="text.secondary">
                {approvedLoans.filter(loan => loan.status === 'approved').length} loans pending action
              </Typography>
            )}
            {currentStep === 1 && (
              <Typography variant="body2" color="text.secondary">
                Record additional group collections
              </Typography>
            )}
          </Box>

          {/* Right Side - Next Button */}
          <Box>
            {currentStep < steps.length - 1 && (
              <Button
                variant="contained"
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={!canProceedToNext() || submitting}
                size="small"
              >
                Next Step
              </Button>
            )}
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PostAllocationDialog;